import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

type Primitive = string | number | boolean | null | undefined;
type SerializableValue = Primitive | { [key: string | number]: SerializableValue } | SerializableValue[];

export const useLocalStorageState = <TState extends SerializableValue>(
    key: string,
    initialValue?: TState,
): [TState | undefined, Dispatch<SetStateAction<TState | undefined>>, VoidFunction] => {
    const initializer = useRef((key: string) => {
        try {
            const localStorageValue = localStorage.getItem(key);

            if (localStorageValue !== null) {
                return JSON.parse(localStorageValue);
            } else {
                initialValue && localStorage.setItem(key, JSON.stringify(initialValue));

                return initialValue;
            }
        } catch {
            // If user is in private mode or has storage restriction
            // localStorage can throw. JSON.parse and JSON.stringify
            // can throw, too.
            return initialValue;
        }
    });

    const [state, setState] = useState<TState | undefined>(() => initializer.current(key));

    useLayoutEffect(() => setState(initializer.current(key)), [key]);

    const set: Dispatch<SetStateAction<TState | undefined>> = useCallback(
        (valOrFunc) => {
            try {
                const newState =
                    typeof valOrFunc === 'function'
                        ? (valOrFunc as (prevState: TState | undefined) => TState | undefined)(state)
                        : valOrFunc;

                if (typeof newState === 'undefined') return;

                const value = JSON.stringify(newState);

                localStorage.setItem(key, value);

                setState(JSON.parse(value));
            } catch {
                // If user is in private mode or has storage restriction
                // localStorage can throw. Also JSON.stringify can throw.
            }
        },
        [key, state],
    );

    const remove = useCallback(() => {
        try {
            localStorage.removeItem(key);
            setState(undefined);
        } catch {
            // If user is in private mode or has storage restriction
            // localStorage can throw.
        }
    }, [key, setState]);

    return [state, set, remove];
};
